import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageThreadModel } from '../models/MessageThreadModel';
import { MessageModel } from '../models/MessageModel';
import { formatDate } from '../utils/dateUtils';
import messageService from '../services/messageService';
import { useAppContext } from '../context/AppContext';
import Styles from './MessagesPage.module.css';
import buyerService from '../services/buyerService';
import itemService from '../services/itemService';
import { ItemModel } from '../models/ItemModel';
import { formatPrice } from '../utils/currencyUtils';
import { Link, useParams } from 'react-router-dom';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import { set } from 'date-fns';
import { useWindowSize } from '../hooks/useWindowSize';
import SidebarComp from '../components/SideBarComp';
import { OfferModel } from '../models/OfferModel';
import offerService from '../services/offerService';
import MessageThreadListComp from '../components/MessageThreadListComp';



const MessagesPage = () => {
    const { threadId } = useParams<{ threadId: string }>();
    const { seller } = useAppContext();
    const [sellerId] = useState(seller!.sellerId); //seller id 
    const { categories } = useReferenceDataContext(); //categories for filtering items

    const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;

    const [selectedThread, setSelectedThread] = useState<MessageThreadModel>();
    const [selectedThreadBuyerName, setSelectedThreadBuyerName] = useState<string>();
    const [selectedItemInfo, setSelectedItemInfo] = useState<ItemModel>();
    const [selectedOfferInfo, setSelectedOfferInfo] = useState<OfferModel>();
    const [messageThreads, setMessageThreads] = useState<MessageThreadModel[]>([]);
    const [messages, setMessages] = useState<MessageModel[]>([]);
    const [filter, setFilter] = useState<'all' | 'read' | 'unread'>('all');

    const [pageNum, setPageNum] = useState(0); //current page number

    const pageSize = 10; //number of items per page
    const [hasMoreThreads, setHasMoreThreads] = useState(false);
    const bottomRef = useRef<HTMLLIElement | null>(null);
    const isLoadingThreadsRef = useRef(false);

    const [message, setMessage] = useState('');
    const [sendEmailCopy, setSendEmailCopy] = useState(true);

    const bottomMessageRef = useRef<HTMLDivElement | null>(null);
    const messageContainerRef = useRef<HTMLDivElement | null>(null);
    const messageInputRef = useRef<HTMLTextAreaElement | null>(null);


    const width = useWindowSize();
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const isMobile = width <= 768;


    const [serviceError, setServiceError] = useState(''); //error for service call

    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false); //indicates if initial load is done
    const [isItemInfoLoaded, setIsItemInfoLoaded] = useState(false);
    const getBuyer = async (buyerId: number) => {
        const buyer = await buyerService.getBuyer(buyerId);
        setSelectedThreadBuyerName(buyer.firstname + " " + buyer.lastname);
    };

    const getReadUnreadFilterValue = (filter: string) => {
        if (filter === 'all') return -1;
        if (filter === 'read') return 0;
        if (filter === 'unread') return 1;
    }

    const getItemInfo = async (itemId: number) => {
        setIsItemInfoLoaded(false); // reset before fetching
        try {
            const item = await itemService.getItem(itemId);
            setSelectedItemInfo(item);
        } catch (e) {
            setSelectedItemInfo(undefined);
        } finally {
            setIsItemInfoLoaded(true); // mark fetch as done
        }
    };

    const getOfferInfo = async (offerId: number) => {
        try {
            const offer = await offerService.getOffer(offerId);
            setSelectedOfferInfo(offer);
        }
        catch (e) {
            setSelectedOfferInfo(undefined);
        }
    }

    const handleFilterChange = (newFilter: 'all' | 'read' | 'unread') => {
        setFilter(newFilter);
        setPageNum(0);
        setMessages([]);
        setSelectedThread(undefined);
        setSelectedThreadBuyerName('');
        setSelectedItemInfo(undefined);
        setMessageThreads([]);
        setHasMoreThreads(true);
    }

    const handleThreadClick = (thread: MessageThreadModel) => {
        if (!thread) return;

        setSelectedThread(thread);
        if (thread.sellerUnread) {
            const markRead = async () => {
                const response = await messageService.markMessageThreadRead(thread.messageThreadId, 0);
                if (response === 'Success') {
                    thread.sellerUnread = false; // Update the thread state locally
                }
            }
            markRead();
        }
        getBuyer(thread.buyerId);

        setSelectedItemInfo(undefined);
        setSelectedOfferInfo(undefined);

        if (thread.itemId) {
            getItemInfo(thread.itemId);
        }
        else if (thread.offerId) {
            getOfferInfo(thread.offerId);
        }

        if (messageInputRef.current) {
            messageInputRef.current.focus();
        }
    }



    const handleSend = () => {
        if (!message.trim() || !selectedThread) return;


        const sentMessage = async () => {
            try {
                setIsSending(true);
                const result = await messageService.sendMessage(selectedThread!.messageThreadId, message, sendEmailCopy);
                if (result.result == 'Success') {
                    setMessages(prev => [...prev, result.message]);
                    setMessage('');
                }
                else if (result.result == 'TooFrequent') {
                    setServiceError('You are sending messages too frequently. Please wait a moment and try again.');
                }
                else if (result.result == 'Error') {
                    setServiceError('Failed to send message. Please try again later.');
                }
                else {
                    setServiceError('Failed to send message. Due to unknown error, Please try again later.');
                }

            }
            catch (e: any) {
                console.error("Error sending message:", e);
                setServiceError(e && e.message || 'Failed to send message. Please try again later.');
            }
            finally {
                setIsSending(false);
            }
        }

        setServiceError('');
        sentMessage();
    };


    const loadThreads = useCallback(async (pageNum: number = 0) => {
        if (isLoadingThreadsRef.current) return;

        isLoadingThreadsRef.current = true;

        try {
            const response = await messageService.getMessageThreads(sellerId, getReadUnreadFilterValue(filter), pageNum * pageSize, pageSize);
            if (response.length === 0) {
                setHasMoreThreads(false);
            }
            else if (response.length < pageSize) {
                setHasMoreThreads(false);
                setMessageThreads(prev => [...prev, ...response]);
            }
            else {
                setHasMoreThreads(true);
                setMessageThreads(prev => [...prev, ...response]);
            }

            if (threadId) {
                const thread = response.find(t => t.messageThreadId === parseInt(threadId));
                if (thread) {
                    handleThreadClick(thread);
                }
            }
        }
        catch (error) {
            console.error("Error loading message threads:", error);
            setServiceError('Failed to load message threads. Please try again later.');
        }
        finally {
            isLoadingThreadsRef.current = false;
            setIsLoading(false);
            setIsLoaded(true); //initial load is done
        }


    }, [sellerId, filter]);

    const loadMessages = useCallback(async (threadId: number) => {
        try {
            const messages = await messageService.getMessages(threadId);
            setMessages(messages);
        }
        catch (error) {
            console.error("Error loading messages:", error);
            setServiceError('Failed to load messages. Please try again later.');
        }

    }, []);



    useEffect(() => {

        if (!isLoaded) {
            setIsLoading(true);
        }
        loadThreads(pageNum);
    }, [pageNum, filter]);

    useEffect(() => {

    }, [filter]);

    useEffect(() => {
        if (selectedThread) {
            loadMessages(selectedThread.messageThreadId);
        }
    }, [selectedThread, loadMessages]);


    // useEffect(() => {
    //     if (selectedThread && (selectedThread.itemId || selectedThread.offerId)) {
    //         setViewReady(true);
    //     }
    // }, [selectedThread]);

    useEffect(() => {
        if (!hasMoreThreads || !bottomRef.current) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    setPageNum(prev => prev + 1);
                }
            },
            { threshold: 1.0 }
        );

        const refCurrent = bottomRef.current;

        // Delay observer to allow layout to settle (helps with large screens)
        const timer = setTimeout(() => {
            if (refCurrent) {
                observer.observe(refCurrent);
            }
        }, 100); // delay 100ms

        return () => {
            clearTimeout(timer);
            if (refCurrent) observer.unobserve(refCurrent);
        };
    }, [bottomRef.current, hasMoreThreads]);

    useEffect(() => {
        const container = document.querySelector('.list-group'); // or messageContainerRef.current
        const shouldLoad = container && container.scrollHeight <= container.clientHeight;

        if (shouldLoad && hasMoreThreads && !isLoadingThreadsRef.current) {
            setPageNum(prev => prev + 1);
        }
    }, [messageThreads, hasMoreThreads]);

    useEffect(() => {
        if (bottomMessageRef.current) {
            bottomMessageRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    return (
        <div className='mt-3 mb-3' style={{ height: 'calc(100vh - 169px)' }}>
            {isLoading && (
                <div className="text-center gap-2 mt-3 text-secondary">
                    <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    <span>  Loading...</span>
                </div>
            )}
            {!isLoading && (<div className="card h-100 rounded-0 pb-4 ">
                <div className="card-body  h-100  p-4">
                    <h5 className="card-title mb-3">Messages</h5>

                    <div className="d-flex h-100 pb-4" >
                        {!isMobile && (
                            <>
                                <div style={{ width: '300px' }} className="d-flex flex-column">
                                    <div className="btn-group mb-1 me-1" role="group" aria-label="Message filter buttons" style={{ marginLeft: '1px' }}>
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnRadioAll"
                                            autoComplete="off"
                                            checked={filter === 'all'}
                                            onChange={() => handleFilterChange('all')}
                                        />
                                        <label className="btn btn-outline-dark btn-sm rounded-0" htmlFor="btnRadioAll" style={{ outline: 'none', boxShadow: 'none' }}>
                                            All
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnRadioRead"
                                            autoComplete="off"
                                            checked={filter === 'read'}
                                            onChange={() => handleFilterChange('read')}
                                        />
                                        <label className="btn btn-outline-dark btn-sm rounded-0" htmlFor="btnRadioRead" style={{ outline: 'none', boxShadow: 'none' }}>
                                            Read
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnRadioUnread"
                                            autoComplete="off"
                                            checked={filter === 'unread'}
                                            onChange={() => handleFilterChange('unread')}
                                        />
                                        <label className="btn btn-outline-dark btn-sm rounded-0" htmlFor="btnRadioUnread" style={{ outline: 'none', boxShadow: 'none' }}>
                                            Unread
                                        </label>
                                    </div>
                                    <ul className="border  mb-0 me-1  list-group rounded-0" style={{ overflowY: 'auto', flex: 1 }}>
                                        {messageThreads.length === 0 ? (
                                            <li className="d-flex flex-column justify-content-center align-items-center text-muted" style={{ height: '150px' }}>
                                                <i className="bi bi-chat-dots fs-2 mb-2"></i>
                                                <span>No messages available.</span>
                                            </li>
                                        ) : (messageThreads.map((thread) => (
                                            <li
                                                key={thread.messageThreadId}
                                                className={`list-group-item border-top-0 border-end-0 border-start-0 border-bottom ${selectedThread?.messageThreadId === thread.messageThreadId ? 'active' : ''} rounded-0`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleThreadClick(thread)}>
                                                <div className={`${thread.sellerUnread ? 'fw-bold' : 'fw-normal'} text-truncate`}>{thread.subject}</div>
                                                {thread.lastMessage && (
                                                    <div className={`text-muted small text-truncate`}>{thread.lastMessage.messageContent}</div>
                                                )}
                                                <div className="text-muted small">{formatDate(thread.lastUpdatedTime)}</div>
                                            </li>
                                        )))}
                                        <li ref={bottomRef} className="list-group-item border-0 text-center text-muted small rounded-0" style={{ cursor: 'pointer' }}>
                                            {hasMoreThreads && (

                                                <div className="d-flex justify-content-center align-items-center gap-2">
                                                    <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                                    <span>Loading more ...</span>
                                                </div>

                                            )}
                                        </li>
                                    </ul>
                                </div>
                            </>)}
                        {isMobile && (<>
                            {!mobileSidebarOpen && (
                                <button className={`position-absolute btn border btn ms-2 mt-2 `} onClick={() => setMobileSidebarOpen(true)}>
                                    <i className="bi bi-list fs-2"></i>
                                </button>)}

                            {mobileSidebarOpen && (
                                <SidebarComp close={() => setMobileSidebarOpen(false)}>
                                    <MessageThreadListComp
                                        messageThreads={messageThreads}
                                        selectedThread={selectedThread ?? null}
                                        filter={filter}
                                        handleThreadClick={(thread) => { handleThreadClick(thread); setMobileSidebarOpen(false); }}
                                        handleFilterChange={handleFilterChange}
                                    >
                                        <li ref={bottomRef} className="list-group-item border-0 text-center text-muted small rounded-0" style={{ cursor: 'pointer' }}>
                                            {hasMoreThreads && (

                                                <div className="d-flex justify-content-center align-items-center gap-2">
                                                    <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                                    <span>Loading more ...</span>
                                                </div>

                                            )}
                                        </li>
                                    </MessageThreadListComp>
                                </SidebarComp>
                            )}
                        </>
                        )}
                        {((isMobile && !mobileSidebarOpen) || (!isMobile)) && (<>
                            <div className="flex-grow-1  d-flex flex-column border">
                                <div className={` p-4 rounded-0 ${isMobile ? 'ms-5' : ''}`}>

                                    <h5>{selectedThread
                                        ? `${selectedThreadBuyerName}  •  ${messageThreads.find((t) => t.messageThreadId === selectedThread.messageThreadId)?.subject}`
                                        : 'Select a message'}</h5>
                                </div>
                                <div className="p-4 flex-grow-1 overflow-auto  d-flex flex-column border-top" ref={messageContainerRef}>
                                    {selectedThread && selectedThread.itemId && isItemInfoLoaded && !selectedItemInfo && !selectedThread.offerId && (
                                        <div className='text-danger small mb-3'>
                                            <i className="bi bi-info-circle-fill"></i> Item in this message is no longer available.
                                        </div>
                                    )}
                                    {selectedThread && selectedItemInfo && (
                                        <>
                                            <div className="text-muted small">Item mentioned in message
                                            </div>
                                            <div className='d-flex align-items-center border mb-4'>
                                                <img
                                                    src={selectedItemInfo.primaryImage ? `${itemImageURL}/${selectedItemInfo.primaryImage}` : "/images/fallback.png"}
                                                    alt={selectedItemInfo.itemName}
                                                    className="img-fluid rounded-start border-0 border-end"
                                                    style={{ height: '100px', width: '100px', objectFit: 'contain' }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.onerror = null;
                                                        target.src = "/images/fallback.png";

                                                    }}

                                                />
                                                <div className='d-flex flex-column g-3 ms-3 flex-grow-1'>
                                                    <div className="fw-bold small mt-2 text-truncate one-line-text-ellipsis">
                                                        {selectedItemInfo.itemName}
                                                    </div>
                                                    <div className='text-muted small'>{formatPrice(selectedItemInfo.price)}</div>

                                                    <div className="text-muted small mb-2 text-truncate one-line-text-ellipsis">
                                                        {selectedItemInfo.description}
                                                    </div>

                                                </div>
                                                <div className="me-2">
                                                    <Link
                                                        to={`/items/view/${selectedItemInfo.itemId}`}
                                                        state={{ categories }}
                                                        className="btn btn-sm btn-outline-dark rounded-0"
                                                        style={{ minWidth: '80px' }}
                                                    >
                                                        View
                                                    </Link>

                                                </div>
                                            </div>
                                        </>

                                    )}

                                    {selectedThread && selectedOfferInfo && (
                                        <>
                                            <div className="text-muted small">Offer mentioned in message
                                            </div>
                                            <div className='d-flex align-items-center border mb-4'>
                                                <img
                                                    src={selectedOfferInfo.itemImage ? `${itemImageURL}/${selectedOfferInfo.itemImage}` : "/images/fallback.png"}
                                                    alt={selectedOfferInfo.itemName}
                                                    className="img-fluid rounded-start border-0 border-end"
                                                    style={{ height: '100px', width: '100px', objectFit: 'contain' }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.onerror = null;
                                                        target.src = "/images/fallback.png";

                                                    }}

                                                />
                                                <div className='d-flex flex-column g-3 ms-3 flex-grow-1'>
                                                    <div className="fw-bold small text-truncate one-line-text-ellipsis">
                                                        {selectedOfferInfo.itemName}
                                                    </div>
                                                    <div className='text-muted small'>Price: {formatPrice(selectedOfferInfo.itemPrice)}</div>
                                                    <div className='text-muted small'>Qty: {selectedOfferInfo.quantity}</div>
                                                    <div className='text-muted small'>Amount: {formatPrice(selectedOfferInfo.amount)}</div>


                                                </div>
                                                <div className="me-2">
                                                    {selectedOfferInfo && (
                                                        <Link
                                                            to={`/offers/view/${selectedOfferInfo.offerId}`}
                                                            state={{ offer: selectedOfferInfo }}
                                                            className="btn btn-sm btn-outline-dark rounded-0"
                                                            style={{ minWidth: '80px' }}
                                                        >
                                                            View
                                                        </Link>
                                                    )}

                                                </div>
                                            </div>
                                        </>

                                    )}

                                    {messages.length > 0 ? (
                                        messages.map((msg) => (
                                            <>
                                                <div key={msg.messageId}
                                                    className={`chat-bubble rounded-3 ps-3 pt-2 pb-2 pe-3 position-relative ${msg.senderRole === 'Buyer' ? 'align-self-start bg-white' : 'align-self-end right'
                                                        }`}
                                                    style={{ maxWidth: '70%' }}
                                                >
                                                    {/* <div className="fw-bold small">
                                                    {msg.senderRole === 'Buyer' ? selectedThreadBuyerName : ''}
                                                </div> */}
                                                    <div style={{ whiteSpace: "pre-line" }}>{msg.messageContent}</div>
                                                    <div className={`chat-tip ${msg.senderRole === 'Buyer' ? 'left' : 'right'}`} />
                                                </div>
                                                <div
                                                    className={`${msg.senderRole === 'Buyer' ? 'text-start' : 'text-end'
                                                        } ps-1 pt-1 text-muted small mb-3`}
                                                >
                                                    {formatDate(msg.createdTime)}
                                                </div>
                                            </>


                                        ))
                                    ) : (
                                        <div className="text-muted"></div>
                                    )}
                                    <div ref={bottomMessageRef} />
                                    {serviceError && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError}</div></div>}

                                </div>

                                <div className='border-top p-3' style={{ height: '150px' }}>
                                    <div className="d-flex flex-column h-100">
                                        <textarea
                                            className="form-control flex-grow-1 mb-2 rounded-0"
                                            placeholder="Type your message..."
                                            style={{ resize: "none" }}
                                            value={message}
                                            ref={messageInputRef}
                                            onChange={(e) => setMessage(e.target.value)}
                                            disabled={isSending || !selectedThread}
                                        />
                                        <div className="d-flex justify-content-between">
                                            <div className="form-check me-auto">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="sendEmailCopyCheckbox"
                                                    checked={sendEmailCopy}
                                                    disabled={!selectedThread || isSending}
                                                    onChange={(e) => setSendEmailCopy(e.target.checked)}
                                                />
                                                <label
                                                    className="form-check-label small text-muted"
                                                    htmlFor="sendEmailCopyCheckbox"
                                                >
                                                    Send buyer an email copy
                                                </label>
                                            </div>

                                            <button
                                                className="btn btn-dark rounded-0 d-flex align-items-center justify-content-center"
                                                type="button"
                                                disabled={!message.trim() || !selectedThread || isSending}
                                                onClick={handleSend}
                                            >
                                                {isSending ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-send me-1"></i> Send
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </>)}



                    </div>
                </div>
            </div>)}

        </div>


    );
};

export default MessagesPage;